import { useEffect, useState } from "react"
import { Navigate, useNavigate } from "react-router-dom"
import { InteractionStatus } from "@azure/msal-browser"
import { useIsAuthenticated, useMsal } from "@azure/msal-react"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSignInAlt } from "@fortawesome/free-solid-svg-icons"

import { Blockquote, Loading, Text, Button } from "@wfp/ui"

import { loginRequestConfigs } from "lib/msal"
import { ReactComponent as Logo } from "assets/logo.svg"
import { useLazyCheckTokenQuery } from "services/core/token"
import { getAccessToken, saveAccessToken, saveRefreshToken } from "utils/token"

const AUTO_LOGIN = true // Set to false to rollback the auto-login bypass and show the login button screen

export default function LoginPage() {
  const { instance, accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const navigate = useNavigate()
  const [checkToken] = useLazyCheckTokenQuery()
  const [fetchingAccessToken, setFetchingAccessToken] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)

  const handleLogin = (): void => {
    setLoginError(null)
    instance.loginRedirect(loginRequestConfigs)
  }

  const handleAzureLogin = async (): Promise<void> => {
    setFetchingAccessToken(true)

    try {
      const { idToken } = await instance.acquireTokenSilent({
        account: accounts[0],
        ...loginRequestConfigs,
      })

      sessionStorage.setItem("azure_token", idToken)
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { access_token, refresh_token, user } = await checkToken(idToken).unwrap()

      sessionStorage.setItem("UserEmail", user?.email)
      saveAccessToken(access_token)
      saveRefreshToken(refresh_token)

      const redirectUrl = sessionStorage.getItem("redirectUrl") || "/market-prices/dashboard"
      sessionStorage.removeItem("redirectUrl")
      navigate(redirectUrl)
    } catch (err) {
      const error = err as Record<string, unknown>
      if (error?.status === 401) {
        navigate("/no-access")
      } else {
        setLoginError("Login failed. Please try again.")
      }
    } finally {
      setFetchingAccessToken(false)
    }
  }

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return

    if (isAuthenticated) {
      handleAzureLogin()
    } else if (AUTO_LOGIN && !loginError) {
      instance.loginRedirect(loginRequestConfigs)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, isAuthenticated, loginError, accounts, instance])

  if (getAccessToken()) {
    return <Navigate to="/market-prices/dashboard" />
  }

  if (inProgress !== InteractionStatus.None || fetchingAccessToken) {
    return <Loading withOverlay small />
  }

  if (!isAuthenticated || loginError) {
    return (
      <div className="h-screen w-screen bg-gray-200 flex justify-center items-center flex-col">
        <div className="flex h-[320px]">
          <div className="bg-white border border-gray-800 p-11 flex flex-col justify-between rounded-l-md">
            <div>
              <Text kind="h1" className="m-0">
                Login
              </Text>
              <Text kind="p" className="text-gray-600">
                Sign In to your account
              </Text>
            </div>

            <div>
              <div className="h-[2px] w-full bg-gray-300" />
              <Button onClick={handleLogin} className="mt-2">
                <FontAwesomeIcon icon={faSignInAlt} className="mr-1" /> <span>Login with WFP credentials</span>
              </Button>
            </div>
          </div>

          <div className="bg-brand-01 border border-brand-01 rounded-r-md px-8 flex-col justify-center items-center hidden lg:flex">
            <Text kind="h1" className="text-white m-0">
              VAM Data Bridges
            </Text>
            <Text kind="p" className="text-white">
              The reporting platform
            </Text>
            <div className="mt-5">
              <Logo />
            </div>
          </div>
        </div>

        {loginError && (
          <Blockquote kind="error" withIcon className="mt-2 absolute bottom-4 right-4 text-sm">
            <p className="py-1">{loginError}</p>
          </Blockquote>
        )}
      </div>
    )
  }

  return null
}
